/**
 * AssessmentTypeCard - Card de seleção de tipo de assessment
 * @module features/assessment-selection/components/AssessmentTypeCard
 *
 * Componente reutilizável para exibir uma opção de tipo de assessment.
 * Inclui estados visuais para hover, focus e seleção.
 *
 * US-020: Redesign da Página Assessment Selection
 */

import { GovernanceIcon, PerformanceIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import type { AssessmentType } from '@/features/chat/types';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface AssessmentTypeCardProps {
  /** Tipo do assessment ('tech' ou 'governance') */
  type: AssessmentType;
  /** Título do card */
  title: string;
  /** Promise/proposta de valor do assessment */
  promise: string;
  /** Lista de 3 pontos de avaliação */
  evaluationPoints: [string, string, string];
  /** Tag "Indicado para..." */
  indicatedFor: string;
  /** Callback chamado ao selecionar este tipo */
  onSelect: (type: AssessmentType) => void;
  /** Indica se este card está selecionado */
  isSelected?: boolean;
  /** Indica se está em estado de loading (transição) */
  isLoading?: boolean;
  /** Controla se a animação deve ser aplicada (evita restart em re-renders) */
  shouldAnimate?: boolean;
  /** Classe CSS adicional para o card */
  className?: string;
  /** Estilos inline (para animationDelay) */
  style?: CSSProperties;
}

// =============================================================================
// ICON MAP
// =============================================================================

const iconMap = {
  tech: PerformanceIcon,
  governance: GovernanceIcon,
} as const;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Card de seleção de tipo de assessment
 *
 * @example
 * ```tsx
 * <AssessmentTypeCard
 *   type="governance"
 *   title="Governança de Produtos"
 *   promise="Descubra se sua organização está preparada para escalar..."
 *   evaluationPoints={['Ponto 1', 'Ponto 2', 'Ponto 3']}
 *   indicatedFor="Indicado para líderes de Produto"
 *   onSelect={(type) => console.log(type)}
 * />
 * ```
 */
export function AssessmentTypeCard({
  type,
  title,
  promise,
  evaluationPoints,
  indicatedFor,
  onSelect,
  isSelected = false,
  isLoading = false,
  shouldAnimate = true,
  className,
  style,
}: AssessmentTypeCardProps) {
  const Icon = iconMap[type];

  const handleClick = () => {
    if (!isLoading) {
      onSelect(type);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && !isLoading) {
      event.preventDefault();
      onSelect(type);
    }
  };

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoading) {
      onSelect(type);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Selecionar diagnóstico ${title}`}
      aria-pressed={isSelected}
      aria-busy={isLoading}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'assessment-card group fade-enter',
        shouldAnimate && 'is-visible',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isSelected && 'border-primary',
        isLoading && 'pointer-events-none',
        className
      )}
      data-testid={`assessment-card-${type}`}
      style={style}
    >
      {/* Ícone */}
      <div className="mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-200">
          <Icon className="w-6 h-6" />
        </div>
      </div>

      {/* Título */}
      <h3 className="text-xl font-semibold text-foreground mb-2">{title}</h3>

      {/* Promise */}
      <p className="text-muted-foreground mb-5 leading-relaxed">{promise}</p>

      {/* Pontos de avaliação */}
      <ul className="space-y-2.5 mb-6">
        {evaluationPoints.map((point, index) => (
          <li key={index} className="flex items-start gap-2.5 text-sm text-foreground">
            <Check
              className="w-4 h-4 text-primary mt-0.5 shrink-0"
              data-testid="check-icon"
              aria-hidden="true"
            />
            <span>{point}</span>
          </li>
        ))}
      </ul>

      {/* Tag indicado para */}
      <p className="text-xs text-muted-foreground mb-5 py-2 px-3 bg-secondary rounded-md inline-block">
        {indicatedFor}
      </p>

      {/* CTA */}
      <Button
        type="button"
        className="w-full"
        onClick={handleButtonClick}
        disabled={isLoading}
        aria-label="Fazer esse diagnóstico"
      >
        Fazer esse diagnóstico
      </Button>

      {/* Loading overlay */}
      {isLoading && (
        <div
          className="absolute inset-0 bg-card/80 flex items-center justify-center rounded-lg"
          role="status"
          aria-label="Carregando"
        >
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}
